import { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { API_URL } from '../utils/formatters';

/**
 * Owns the main transactions listing + filters + investor lists +
 * the "transactions" tab sort/selection.
 *
 * Preserves the parent's exact semantics:
 *   • Same useCallback dep arrays
 *   • Same auto-fetch on mount for transactions AND investors
 *   • selectedTxns is preserved even though only ever cleared inside
 *     fetchTransactions (matches parent — do not "dead-code eliminate").
 */
export function useTransactionsData({ token, headers }) {
  const [loading, setLoading] = useState(true);
  const [transactions, setTransactions] = useState([]);
  const [investors, setInvestors] = useState([]);
  const [reconInvestors, setReconInvestors] = useState([]);
  // rp_codes with NOTHING left to reconcile — powers the green tick next
  // to an investor's name in the reconciliation picker so an admin knows
  // to skip them. Refetched whenever the investor list reloads; a few
  // minutes stale is fine for a "should I even open this one" hint.
  const [reconClearIds, setReconClearIds] = useState(new Set());
  const [pagination, setPagination] = useState({ page: 1, pageSize: 500, total: 0, totalPages: 0 });
  const [summary, setSummary] = useState({ total_investments: 0, total_withdrawals: 0, total_distributions: 0 });
  const [investorFilter, setInvestorFilter] = useState('all');
  const [typeFilter, setTypeFilter] = useState('all');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [sortTxn, setSortTxn] = useState({ key: 'transaction_date', dir: 'desc' });
  const [selectedTxns, setSelectedTxns] = useState(new Set());

  const fetchTransactions = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      params.append('page', pagination.page);
      params.append('page_size', pagination.pageSize);
      if (sortTxn?.key) {
        params.append('sort_by', sortTxn.key);
        params.append('sort_order', sortTxn.dir || 'desc');
      }
      if (investorFilter && investorFilter !== 'all') params.append('investor_id', investorFilter);
      if (typeFilter && typeFilter !== 'all') params.append('transaction_type', typeFilter);
      if (startDate) params.append('start_date', startDate);
      if (endDate) params.append('end_date', endDate);
      const response = await axios.get(`${API_URL}/api/admin/investor-transactions?${params}`, { headers });
      setTransactions(response.data.items);
      setSelectedTxns(new Set());
      setPagination(prev => ({ ...prev, total: response.data.total, totalPages: response.data.total_pages }));
      setSummary(response.data.summary || {});
    } catch (error) {
      toast.error('Failed to load transactions');
    } finally {
      setLoading(false);
    }
  }, [token, pagination.page, pagination.pageSize, investorFilter, typeFilter, startDate, endDate, sortTxn?.key, sortTxn?.dir]);

  // Green-tick list for the Reconciliation investor picker. Re-run after
  // every reconciliation refresh (2026-10-08) — it used to load once with the
  // page, so an investor reconciled during the session never got its tick
  // until a full page reload.
  const reconInvestorListRef = useRef([]);
  const fetchReconTicks = useCallback(async () => {
    try {
      const r3 = await axios.get(`${API_URL}/api/admin/investor-transactions/reconciliation/unmatched-summary`, { headers });
      const withUnmatched = new Set(r3.data.rp_codes_with_unmatched || []);
      // Invert against the investor list so InvestorPicker only needs a
      // single "clear" Set to check against.
      const clear = new Set(
        reconInvestorListRef.current
          .filter(inv => !withUnmatched.has(inv.id) && !withUnmatched.has(inv.rp_code))
          .map(inv => inv.id)
      );
      setReconClearIds(clear);
    } catch { /* silent — matches parent */ }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  const fetchInvestors = useCallback(async () => {
    try {
      const response = await axios.get(`${API_URL}/api/admin/investors?limit=1000`, { headers });
      setInvestors(response.data.items || []);
    } catch { /* silent — matches parent */ }
    // Also load RP-code-based list for the Reconciliation tab dropdown
    let reconInvestorList = [];
    try {
      const r2 = await axios.get(`${API_URL}/api/admin/statements/selectable-investors`, { headers });
      reconInvestorList = r2.data.investors || [];
      setReconInvestors(reconInvestorList);
    } catch { /* silent — matches parent */ }
    reconInvestorListRef.current = reconInvestorList;
    await fetchReconTicks();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  const clearFilters = () => {
    setInvestorFilter('all');
    setTypeFilter('all');
    setStartDate('');
    setEndDate('');
    setPagination(prev => ({ ...prev, page: 1 }));
  };

  useEffect(() => { fetchTransactions(); }, [fetchTransactions]);
  useEffect(() => { fetchInvestors(); }, [fetchInvestors]);

  return {
    loading,
    transactions,
    investors,
    reconInvestors,
    reconClearIds,
    pagination, setPagination,
    summary,
    investorFilter, setInvestorFilter,
    typeFilter, setTypeFilter,
    startDate, setStartDate,
    endDate, setEndDate,
    sortTxn, setSortTxn,
    selectedTxns, setSelectedTxns,
    fetchTransactions,
    fetchInvestors,
    fetchReconTicks,
    clearFilters,
  };
}
